import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api/client';
import logoIcon from '../assets/logo-icon.svg';

export default function Login() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [error, setError] = useState('');
  const [isExchanging, setIsExchanging] = useState(false);
  const processedCallbackRef = useRef(null); // guard against StrictMode double-invoke
  const [loginConfig, setLoginConfig] = useState({
    ssoEnabled: false,
    standardLoginEnabled: true,
    loading: true
  });

  // Check login configuration on mount
  useEffect(() => {
    const checkLoginConfig = async () => {
      try {
        console.log('🔍 Checking login config...');
      const response = await authAPI.getLoginConfig();
        console.log('✅ Login config response:', response);
        setLoginConfig({
          ssoEnabled: response.data.ssoEnabled,
          standardLoginEnabled: response.data.standardLoginEnabled,
          loading: false
        });
        console.log('📝 SSO Enabled:', response.data.ssoEnabled, 'Standard Login:', response.data.standardLoginEnabled);
        
        // SSO-only: the Microsoft button renders automatically via loginConfig.ssoEnabled
      } catch (err) {
        console.error('❌ Failed to check login config:', err);
        // Default to SSO login if config check fails — ensures the button always shows
        setLoginConfig({
          ssoEnabled: true,
          standardLoginEnabled: false,
          loading: false
        });
      }
    };
    
    checkLoginConfig();
  }, []);

  // Check for URL parameters (errors, pending approval, callback)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorMsg = params.get('error');
    const pending = params.get('pending');
    const code = params.get('code');
    const token = params.get('token');

    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
    } else if (pending === 'true') {
      setError('Your account is pending approval. Please contact your administrator.');
    } else if (code) {
      // SECURITY C-2: backend sends a one-time exchange code — redeem immediately.
      // Guard against React StrictMode double-invoke: skip if we already processed this code.
      if (processedCallbackRef.current === code) return;
      processedCallbackRef.current = code;
      setIsExchanging(true);
      const isNewUser = params.get('newUser') === 'true';
      (async () => {
        try {
          const { default: apiClient } = await import('../api/client');
          const res = await apiClient.get(`/auth/sso-exchange?code=${encodeURIComponent(code)}`);
          const { token: jwt } = res.data;
          if (!jwt || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(jwt)) {
            setIsExchanging(false);
            setError('Invalid sign-in token from SSO exchange');
            return;
          }
          handleOAuthCallback(jwt, isNewUser);
        } catch (err) {
          console.error('SSO exchange error:', err);
          setIsExchanging(false);
          setError('Sign-in failed. Please try again.');
        }
      })();
    } else if (token) {
      // Legacy direct-token flow (kept for backward-compat during rollout)
      if (processedCallbackRef.current === token) return;
      processedCallbackRef.current = token;
      if (!/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
        setError('Invalid sign-in token');
        return;
      }
      setIsExchanging(true);
      handleOAuthCallback(token, params.get('newUser') === 'true');
    }
  }, [location]);

  const handleOAuthCallback = async (token, isNewUser) => {
    try {
      // Store token first
      localStorage.setItem('token', token);
      
      // Fetch user profile with role information
      const response = await authAPI.me();

      if (response && response.data) {
        // Store user data including role
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Store company ID if available
        if (response.data.currentCompanyId) {
          localStorage.setItem('currentCompanyId', response.data.currentCompanyId);
        }
        
        // Show welcome message for new users
        if (isNewUser) {
          console.log('Welcome! Your account has been created.');
        }
        
        // Reload to trigger auth context update with fresh user data
        window.location.href = '/';
      } else {
        setError('Failed to load user profile');
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('OAuth callback error:', err);
      setError('Authentication failed');
      localStorage.removeItem('token');
    }
  };

  const handleAzureLogin = () => {
    // Redirect directly to Azure login
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    window.location.href = `${apiBaseUrl}/auth/azure`;
  };

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // While redeeming the one-time SSO exchange code show a full-page spinner.
  // This prevents the login form (and any error flash) from appearing during the redirect.
  if (isExchanging) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A2E4D]">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 border-4 border-[#5EBECF]/30 border-t-[#5EBECF] rounded-full animate-spin"></div>
          </div>
          <p className="text-[#5EBECF] text-sm font-medium">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A2E4D] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#5EBECF] rounded-full mix-blend-soft-light filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#5EBECF] rounded-full mix-blend-soft-light filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#F4C542] rounded-full mix-blend-soft-light filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Login Card */}
      <div className="relative z-10 max-w-md w-full mx-4">
        <div className="bg-white rounded-3xl shadow-2xl p-10 space-y-6">
          
          {/* Logo and Header */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="hover:scale-105 transition-transform duration-300">
                <img src={logoIcon} width="120" height="120" alt="SpocoDoc" />
              </div>
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-[#0A2E4D] mb-1">
                Spoco<span style={{ color: '#4f46e5' }}>Doc</span>
              </h1>
              <p className="text-gray-500 text-sm">
                Invoice Management System
              </p>
            </div>
          </div>

          {loginConfig.loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-[#5EBECF]/30 border-t-[#5EBECF] rounded-full animate-spin"></div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg shadow-sm animate-shake">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {/* SSO Login */}
              {loginConfig.ssoEnabled && (
                <div>
                  <button
                    type="button"
                    onClick={handleAzureLogin}
                    className="w-full group relative flex items-center justify-center px-8 py-4 bg-[#0A2E4D] hover:bg-[#163B5E] text-white rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#5EBECF]/30"
                  >
                    <div className="flex items-center space-x-3">
                      <svg className="w-6 h-6" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 0h10.931v10.931H0V0z" fill="#f25022"/>
                        <path d="M12.069 0H23v10.931H12.069V0z" fill="#00a4ef"/>
                        <path d="M0 12.069h10.931V23H0V12.069z" fill="#7fba00"/>
                        <path d="M12.069 12.069H23V23H12.069V12.069z" fill="#ffb900"/>
                      </svg>
                      <span className="font-semibold text-lg">Sign in with Microsoft</span>
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                  
                  <p className="mt-4 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-[#5EBECF]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Secure authentication powered by Microsoft Azure AD
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="pt-6 border-t border-gray-200">
            <p className="text-center text-xs text-gray-500">
              © {new Date().getFullYear()} SpocoDoc. All rights reserved.
            </p>
          </div>
        </div>

        {/* Decorative element below card */}
        <div className="mt-6 text-center">
          <p className="text-white text-base font-semibold tracking-wide drop-shadow-lg">
            Streamline your invoice management workflow
          </p>
        </div>
      </div>
    </div>
  );
}
